// Logger central de la app.
//
// Consola:
//  - En desarrollo emite todo (debug/info/warn/error).
//  - En producción solo warn/error.
//  - Override con REACT_APP_LOG_LEVEL = debug|info|warn|error|silent.
//
// Backend:
//  - Los niveles >= BACKEND_MIN_LEVEL (por defecto "error") se envían best-effort
//    a `api/_Auditoria/Front` (POST JSON, fire-and-forget).
//  - El envío NUNCA lanza, NUNCA escribe en consola y se auto-limita para no inundar
//    (tope por carga de página + deduplicación por ventana de tiempo).
//  - Si el endpoint todavía no existe en el backend, el 404 se ignora en silencio.

import appVar from "../dataVar/appVar";
import { APP_VERSION } from "../appVersion";

const LEVELS = { debug: 10, info: 20, warn: 30, error: 40, silent: 99 };

const isProd = process.env.NODE_ENV === "production";
const configured = (process.env.REACT_APP_LOG_LEVEL || "").toLowerCase();
const consoleMinLevel = LEVELS[configured] ?? (isProd ? LEVELS.warn : LEVELS.debug);

// --- Configuración del reporte al backend ---
const BACKEND_MIN_LEVEL = LEVELS.error;
const BACKEND_ENDPOINT = "api/_Auditoria/Front";
const MAX_BACKEND_LOGS = 25;      // tope de envíos por carga de página
const DEDUPE_WINDOW_MS = 10000;   // no reenviar la misma firma dentro de esta ventana

let backendLogCount = 0;
const recentSignatures = new Map(); // firma -> timestamp

const emitConsole = (level, method, args) => {
    if (LEVELS[level] < consoleMinLevel) return;
    // eslint-disable-next-line no-console
    console[method](...args);
};

const stringifyArg = (a) => {
    if (a instanceof Error) return a.stack || `${a.name}: ${a.message}`;
    if (typeof a === "string") return a;
    try {
        return JSON.stringify(a);
    } catch {
        return String(a);
    }
};

// Hora local de pared ("YYYY-MM-DD HH:mm:ss.SSS"), sin zona — se guarda tal cual
// en la tabla, igual que FechaServer (evita el corrimiento de UTC).
const localTimestamp = () => {
    const d = new Date();
    const p = (n, w = 2) => String(n).padStart(w, "0");
    return (
        `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
        `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`
    );
};

const reportToBackend = (level, args) => {
    try {
        if (LEVELS[level] < BACKEND_MIN_LEVEL) return;
        if (backendLogCount >= MAX_BACKEND_LOGS) return;

        const base = appVar.gApiUrl;
        if (!base) return; // config.json aún no cargada

        const mensaje = typeof args[0] === "string" ? args[0] : stringifyArg(args[0]);
        const detalle = args.slice(1).map(stringifyArg).join("\n");

        const firma = `${level}|${mensaje}`;
        const now = Date.now();
        const last = recentSignatures.get(firma);
        if (last && now - last < DEDUPE_WINDOW_MS) return;
        recentSignatures.set(firma, now);

        backendLogCount += 1;

        const payload = {
            nivel: level,
            mensaje: mensaje.slice(0, 500),
            detalle: detalle.slice(0, 4000),
            ruta:
                typeof window !== "undefined"
                    ? window.location.hash || window.location.pathname
                    : "",
            usuario: appVar.gCodUsuario || "",
            regional: appVar.gCodRegional || "",
            version: APP_VERSION,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
            fecha: localTimestamp(),
        };

        const headers = { "Content-Type": "application/json" };
        if (appVar.gTokenApi) {
            headers.Authorization = `Bearer ${appVar.gTokenApi}`;
        }

        // Fire-and-forget: cualquier fallo (incluido 404 si el endpoint no existe) se ignora.
        fetch(`${base}/${BACKEND_ENDPOINT}`, {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => {});
    } catch {
        // El logging nunca debe romper la app.
    }
};

const make = (level, method) => (...args) => {
    emitConsole(level, method, args);
    reportToBackend(level, args);
};

const logger = {
    debug: make("debug", "log"),
    info: make("info", "info"),
    warn: make("warn", "warn"),
    error: make("error", "error"),
};

// Expuesto para pruebas/soporte desde la consola del navegador:
//   __logger.error("Prueba manual", new Error("test"))
if (typeof window !== "undefined") {
    window.__logger = logger;
}

export default logger;
